import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { change, Field, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { Button, FormGroup, ButtonToolbar } from 'reactstrap';
import PersonSelect from './select/PersonSelect';
import FormItem from './FormItem';
import {
  addPersonInputFocus,
  clearFocusCustomer,
  getPersons,
  patchCustomer,
  postCustomer,
  setFocusCustomer,
  setPassengerList,
  setPersonInputFocus,
} from '../../../actions';
import Modal from '../modal/Modal';
import CustomerMinimalForm from '../../units/user/customer/CustomerMinimalForm';
import { DOCUMENT_TYPE } from '../../../config/constants';
import { tzNormalizeDate } from '../../../utils/date';
import { INT_DATE_FORMAT } from '../../../config/locale';
import { customerGenerator } from '../../../utils/app/json-generator-from-reducer';
import { isBlank } from '../../../utils/validators';
import CustomerResource from '../resource/CustomerResource';
import { debounce } from '../../../utils/promise';

const SEARCH_WAIT_TIME = 300;

const FIELD_LABEL = {
  firstName: 'nombre',
  lastName: 'apellido',
  identificationTypeId: 'tipo de documento',
  idDocumentNumber: 'número de documento',
  dob: 'fecha de nacimiento',
};

const getPersonMissingFields = (person) => {
  const all = [
    ['firstName', FIELD_LABEL.firstName],
    ['lastName', FIELD_LABEL.lastName],
    ['identificationTypeId', FIELD_LABEL.identificationTypeId],
    ['idDocumentNumber', FIELD_LABEL.idDocumentNumber],
    ['dob', FIELD_LABEL.dob],
  ];
  if (!person) return all.map(([, label]) => label);

  const missing = [];
  for (let i = 0; i < all.length; i += 1) {
    const key = all[i][0];
    const label = all[i][1];
    if (isBlank(person[key])) missing.push(label);
  }
  return missing;
};

const PersonInputGroup = ({
  label,
  dispatchGetPersons,
  dispatchChange,
  dispatchSetPersonInputFocus,
  options,
  index,
  name,
  form,
  addPassengerToList,
  fieldsLength,
  dispatchSetFocusCustomer,
  dispatchClearFocusCustomer,
  dispatchAddPersonInputFocus,
  dispatchPostCustomer,
  onChange,
  passengerValue,
  dispatchPatchCustomer,
  passengerList,
  dispatchSetPassengerList,
  ...props
}) => {
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('Registrar nuevo ');
  const [modalBody, setModalBody] = useState(null);
  const [idDocumentNumber, setIdDocumentNumber] = useState('');
  const [showDetailBody, setShowDetailBody] = useState(null);

  const handleCloseModal = () => {
    setShowModal(false);
    setModalBody(null);
  };

  const handleValueChange = (selectedOption) => {
    if (!selectedOption) return;
    if (onChange) {
      onChange(selectedOption);
    }
  };

  const debouncedGetPersons = useRef(
    debounce(
      (documentNumber) => dispatchGetPersons({ document: documentNumber }),
      SEARCH_WAIT_TIME,
    ),
  ).current;

  const handleInputChange = (inputValue) => {
    if (!inputValue || inputValue.length < 5) return;
    setIdDocumentNumber(inputValue);
    debouncedGetPersons(inputValue);
  };

  const onSubmitCustomer = async (formValues, isEditCustomer) => {
    const newFormValues = {
      ...formValues,
      dob: tzNormalizeDate({
        date: formValues.dateOfBirth,
        format: INT_DATE_FORMAT,
        addTime: { amount: 5, unit: 'h' },
      }),
      idCountryOfOrigin: formValues.idCountryOfOrigin.value,
      identificationTypeId: formValues.identificationTypeId.value,
    };

    const response = isEditCustomer
      ? await dispatchPatchCustomer(passengerValue.id, newFormValues)
      : await dispatchPostCustomer(newFormValues, false);

    const source = 'CUSTOMER';

    if (response) {
      const person = {
        ...customerGenerator(response),
        source,
        identificationTypeName: response.identificationType.name,
        identificationTypeId: response.identificationType.id,
        value: `${response.id}-${source}`,
      };
      if (passengerValue && passengerValue.source === 'PERSON') {
        const newPassengerList = passengerList.filter(
          (p) => p.value !== passengerValue.value,
        );
        newPassengerList.push(person);
        dispatchSetPassengerList(newPassengerList);
        dispatchChange(form, 'customer', null);
      }

      if (passengerValue == null && addPassengerToList) {
        addPassengerToList(person);
      }

      dispatchChange(form, name, person);

      if (fieldsLength === 1) {
        dispatchChange(form, 'customer', person);
      }
    }

    handleCloseModal();
  };

  const renderPersonMissing = (person) => {
    if (!person) return null;
    const missing = getPersonMissingFields(person, FIELD_LABEL);
    if (missing.length > 0) {
      return (
        <small className="text-danger">Faltan: {missing.join(', ')}.</small>
      );
    }
    return null;
  };

  const handleShowModal = () => {
    const title = 'Registrar nuevo pasajero';
    setModalTitle(title);
    const body = (
      <CustomerMinimalForm
        onSubmit={(formValues) => onSubmitCustomer(formValues, false)}
        initialValues={{
          idDocumentNumber,
          identificationTypeId: DOCUMENT_TYPE.DNI,
        }}
      />
    );
    setModalBody(body);
    setShowModal(true);
  };

  const handleNoCustomer = () => (
    <div>
      <p>No existe ningún Pasajero con ese nombre o número de documento.</p>
      <Button type="button" color="primary" onClick={() => handleShowModal()}>
        Nuevo Pasajero
      </Button>
    </div>
  );

  const modal = (
    <Modal
      show={showModal}
      title={`${modalTitle} ${label}`}
      onClickClose={handleCloseModal}
      body={modalBody}
      size="lg"
    />
  );

  const onClickEditCustomer = () => {
    if (!passengerValue || !passengerValue.id) return;

    setShowDetailBody(null);

    const title = 'Editar pasajero';
    setModalTitle(title);

    const isEditCustomer =
      passengerValue &&
      passengerValue.source === 'CUSTOMER' &&
      passengerValue.id;

    let identificationTypeId = null;

    const idCountryOfOrigin =
      passengerValue.countryCode && passengerValue.countryName
        ? {
            value: passengerValue.countryCode,
            label: passengerValue.countryName,
          }
        : null;

    const dateOfBirth = passengerValue.dob
      ? tzNormalizeDate({
          date: passengerValue.dob,
          format: INT_DATE_FORMAT,
          addTime: { amount: 5, unit: 'h' },
        })
      : null;

    if (isEditCustomer) {
      identificationTypeId =
        passengerValue.identificationTypeId &&
        passengerValue.identificationTypeName
          ? {
              value: passengerValue.identificationTypeId,
              label: passengerValue.identificationTypeName,
            }
          : null;
    } else {
      identificationTypeId = DOCUMENT_TYPE.DNI;
    }

    const body = (
      <CustomerMinimalForm
        onSubmit={(formValues) => onSubmitCustomer(formValues, isEditCustomer)}
        initialValues={{
          idDocumentNumber: passengerValue.idDocumentNumber,
          firstName: passengerValue.firstName,
          lastName: passengerValue.lastName,
          identificationTypeId,
          idCountryOfOrigin,
          dateOfBirth,
        }}
      />
    );
    setModalBody(body);
    setShowModal(true);
  };

  const onClickViewDataCustomer = () => {
    if (!passengerValue || !passengerValue.value) return;

    let identificationType = null;
    if (passengerValue.source === 'CUSTOMER') {
      identificationType = passengerValue.identificationTypeName
        ? { name: passengerValue.identificationTypeName }
        : null;
    } else {
      identificationType = { name: DOCUMENT_TYPE.DNI.label };
    }

    const personData = {
      ...passengerValue,
      identificationType,
    };

    const body = <CustomerResource customer={personData} formatDob={false} />;

    if (showDetailBody !== null) {
      setShowDetailBody(null);
      return;
    }

    setShowDetailBody(body);
  };

  const detailsButton = passengerValue && (
    <ButtonToolbar className="mt-2">
      <div>
        <Button
          color="primary"
          outline
          size="sm"
          type="button"
          onClick={onClickViewDataCustomer}
        >
          {showDetailBody === null ? (
            <>
              <i className="fa fa-eye" /> Ver Datos
            </>
          ) : (
            <>
              <i className="fa fa-eye-slash" /> Ocultar Datos
            </>
          )}
        </Button>{' '}
        <Button color="primary" outline size="sm" onClick={onClickEditCustomer}>
          <i className="fa fa-edit" /> Editar
        </Button>
      </div>
    </ButtonToolbar>
  );

  return (
    <>
      {modal}
      <FormGroup row>
        <FormItem label={label} required>
          <Field
            onInputChange={handleInputChange}
            component={PersonSelect}
            placeholder="Ingrese Número de Documento"
            isMulti={false}
            noOptionsMessage={() => handleNoCustomer()}
            index={index}
            name={name}
            form={form}
            onChange={handleValueChange}
            {...props}
          />
          {renderPersonMissing(passengerValue)}
          {detailsButton}
          {showDetailBody}
        </FormItem>
      </FormGroup>
    </>
  );
};

PersonInputGroup.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  dispatchGetPersons: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      idDocumentNumber: PropTypes.string,
      identificationTypeId: PropTypes.string,
      identificationTypeName: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      source: PropTypes.string,
    }),
  ).isRequired,
  index: PropTypes.number.isRequired,
  name: PropTypes.string,
  form: PropTypes.string.isRequired,
  dispatchSetPersonInputFocus: PropTypes.func.isRequired,
  addPassengerToList: PropTypes.func,
  fieldsLength: PropTypes.number.isRequired,
  dispatchSetFocusCustomer: PropTypes.func.isRequired,
  dispatchClearFocusCustomer: PropTypes.func.isRequired,
  dispatchAddPersonInputFocus: PropTypes.func.isRequired,
  dispatchPostCustomer: PropTypes.func.isRequired,
  passengerValue: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
    idDocumentNumber: PropTypes.string,
    identificationTypeId: PropTypes.string,
    identificationTypeName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    source: PropTypes.string,
    dob: PropTypes.string,
    idCountryOfOrigin: PropTypes.string,
    id: PropTypes.number,
    countryCode: PropTypes.string,
    countryName: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
  dispatchPatchCustomer: PropTypes.func.isRequired,
  passengerList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      idDocumentNumber: PropTypes.string,
      identificationTypeId: PropTypes.string,
      identificationTypeName: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      source: PropTypes.string,
    }),
  ).isRequired,
  dispatchSetPassengerList: PropTypes.func.isRequired,
};

PersonInputGroup.defaultProps = {
  label: '',
  onChange: undefined,
  name: undefined,
  passengerValue: undefined,
  addPassengerToList: null,
};

const mapStateToProps = (state, { name, form }) => {
  const selector = formValueSelector(form);

  return {
    options: state.UserUnit.Person.getIn(['all', 'content', 'content']).map(
      (p) => {
        const firstName = p.firstName ? p.firstName : '';
        const lastName = p.lastName ? p.lastName : '';
        const idDocumentNumber = p.idDocumentNumber ? p.idDocumentNumber : '';

        let identificationTypeName = null;
        let identificationTypeId = null;
        let idCountryOfOrigin = null;

        if (p.source === 'CUSTOMER') {
          identificationTypeName = p.identificationTypeName
            ? p.identificationTypeName
            : '-';
          identificationTypeId = p.identificationTypeId
            ? p.identificationTypeId
            : '-';
          idCountryOfOrigin = p.idCountryOfOrigin ? p.idCountryOfOrigin : '-';
        } else {
          identificationTypeName = DOCUMENT_TYPE.DNI.label;
          identificationTypeId = DOCUMENT_TYPE.DNI.value;
          idCountryOfOrigin = 'PE';
        }

        const { id, source, dob, countryCode, countryName, mobilePhone } = p;

        return {
          value: `${id}-${source}`,
          label: `${firstName} ${lastName} | ${identificationTypeName}: ${idDocumentNumber} (${idCountryOfOrigin})`,
          idDocumentNumber,
          identificationTypeName,
          identificationTypeId,
          firstName,
          lastName,
          id,
          source,
          dob,
          idCountryOfOrigin,
          countryCode,
          countryName,
          mobilePhone,
        };
      },
    ),
    focusIndex: state.UserUnit.Person.getIn(['inputFocus', 'index']),
    passengerValue: selector(state, name),
    passengerList: state.UserUnit.Person.getIn(['all', 'passengerList']).toJS(),
  };
};

const mapDispatchToProps = {
  dispatchGetPersons: getPersons,
  dispatchChange: change,
  dispatchSetPersonInputFocus: setPersonInputFocus,
  dispatchSetFocusCustomer: setFocusCustomer,
  dispatchClearFocusCustomer: clearFocusCustomer,
  dispatchAddPersonInputFocus: addPersonInputFocus,
  dispatchPostCustomer: postCustomer,
  dispatchPatchCustomer: patchCustomer,
  dispatchSetPassengerList: setPassengerList,
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonInputGroup);
